// openEHR RM model — BMM package org.openehr.rm.ehr_extract.message
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { AUDIT_DETAILS, PARTY_PROXY } from "../common/generic.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Abstract parent of message payload types.
 */
export abstract class MESSAGE_CONTENT {
}

/**
 * A “message” is an authored, possibly signed, piece of content intended for one or more recipients. Since the recipient may or may not be known directly, recipients are specified in the \`ADDRESSED_MESSAGE\` class.
 */
export class MESSAGE {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Details of who actually created the message and when. This is the person who entered the data or otherwise caused the message to be created, or might be a piece of software.
   */
  audit?: AUDIT_DETAILS;
  /**
   * Party responsible for the message content, who may or may not be technically responsible for its creation (e.g. by data entry etc).
   */
  author?: PARTY_PROXY;
  /**
   * Content of the message.
   */
  content?: MESSAGE_CONTENT;
  /**
   * Internal storage for signature
   * @protected
   */
  protected _signature?: openehr_base.String;

  /**
   * Optional signature by the author of message content in openPGP format. The signature is created as a Hash and optional signing of the serialisation of this message object with this signature field Void.
   */
  get signature(): string | undefined {
    return this._signature?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for signature.
   * Use this to access openehr_base.String methods.
   */
  get $signature(): openehr_base.String | undefined {
    return this._signature;
  }

  /**
   * Sets signature from either a primitive value or openehr_base.String wrapper.
   */
  set signature(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._signature = undefined;
    } else if (typeof val === "string") {
      this._signature = openehr_base.String.from(val);
    } else {
      this._signature = val;
    }
  }
}

/**
 * The concept of a message addressed to nominated recipients.
 */
export class ADDRESSED_MESSAGE {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Internal storage for sender
   * @protected
   */
  protected _sender?: openehr_base.String;

  /**
   * Party sending the message.
   */
  get sender(): string | undefined {
    return this._sender?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for sender.
   * Use this to access openehr_base.String methods.
   */
  get $sender(): openehr_base.String | undefined {
    return this._sender;
  }

  /**
   * Sets sender from either a primitive value or openehr_base.String wrapper.
   */
  set sender(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._sender = undefined;
    } else if (typeof val === "string") {
      this._sender = openehr_base.String.from(val);
    } else {
      this._sender = val;
    }
  }

  /**
   * Internal storage for sender_reference
   * @protected
   */
  protected _sender_reference?: openehr_base.String;

  /**
   * Identification of message used by sender. This will be the same no matter how many times this message is sent to these recipients.
   */
  get sender_reference(): string | undefined {
    return this._sender_reference?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for sender_reference.
   * Use this to access openehr_base.String methods.
   */
  get $sender_reference(): openehr_base.String | undefined {
    return this._sender_reference;
  }

  /**
   * Sets sender_reference from either a primitive value or openehr_base.String wrapper.
   */
  set sender_reference(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._sender_reference = undefined;
    } else if (typeof val === "string") {
      this._sender_reference = openehr_base.String.from(val);
    } else {
      this._sender_reference = val;
    }
  }

  /**
   * Intended recipients, in the form of internet addresses.
   */
  addressees?: undefined;
  /**
   * Internal storage for urgency
   * @protected
   */
  protected _urgency?: openehr_base.Integer;

  /**
   * Urgency with which destination should deal with message:
   *
   * * -1 - low
   * * 0 - normal
   * * 1 - high
   */
  get urgency(): number | undefined {
    return this._urgency?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for urgency.
   * Use this to access openehr_base.Integer methods.
   */
  get $urgency(): openehr_base.Integer | undefined {
    return this._urgency;
  }

  /**
   * Sets urgency from either a primitive value or openehr_base.Integer wrapper.
   */
  set urgency(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._urgency = undefined;
    } else if (typeof val === "number") {
      this._urgency = openehr_base.Integer.from(val);
    } else {
      this._urgency = val;
    }
  }

  /**
   * The content of the message.
   */
  message?: MESSAGE;
}
